import { useState } from 'react';
import { Button, Dialog, FileInfo, FileUpload } from '@applyboard/crystal-ui';
import { useTranslation } from '@applyboard/ui-utils';

export type UploadStatus = 'uploading' | 'uploaded' | 'failed';

export const MAX_FILE_SIZE = 5_000_000;

type UploadDialogProps = {
  onClose: () => void;
  open: boolean;
  onUpload: (files: FileInfo[]) => void;
};

const ALLOWED_FILE_TYPES = ['.jpeg', '.png', '.pdf', '.jpg'];

function isFileValid(fileInfo: FileInfo) {
  const validFileSize = fileInfo.file.size <= MAX_FILE_SIZE;
  return (
    validFileSize && ALLOWED_FILE_TYPES.some(ft => fileInfo.file.name.toLowerCase().endsWith(ft))
  );
}

export const UploadDialog = ({ onClose, open, onUpload }: UploadDialogProps) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileInfo[]>([]);
  const filesValid = files && files.length > 0 ? files.every(file => isFileValid(file)) : false;

  return (
    <Dialog onOpenChange={onClose} size="md" open={open}>
      <Dialog.Heading>Upload Files</Dialog.Heading>
      <Dialog.Content>
        <FileUpload
          helpText="Acceptable file formats are PDF, PNG, JPG or JPEG. File size limit is 5 MB."
          maxFileSize={MAX_FILE_SIZE}
          multiple={true}
          allowedFileTypes={ALLOWED_FILE_TYPES}
          onChange={_files => setFiles([..._files])}
          onRemoveFile={_file =>
            setFiles([...files.filter(file => file.file.name !== _file.file.name)])
          }
          size="sm"
          value={files}
        >
          <FileUpload.Button>{t('common.browseFiles')}</FileUpload.Button>
        </FileUpload>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton width="fill">Close</Dialog.CloseButton>
        <Button
          intent="primary"
          disabled={!filesValid}
          onClick={() => onUpload(files)}
          width="fill"
        >
          {t('common.submit')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
